import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import type {
  CrmNotificationRule,
  CrmNotificationSettings,
} from "hooks/use-crm-notification-settings-api";
import type {
  SlackChannel,
  SlackIntegration,
} from "hooks/use-slack-integration-api";
import { AlertTriangle, CheckCircle, ExternalLink } from "lucide-react";
import type React from "react";

type CrmNotificationSettingsPageProps = {
  settings: CrmNotificationSettings | null;
  settingsLoading: boolean;
  slackIntegration: SlackIntegration | null;
  slackLoading: boolean;
  channels: SlackChannel[];
  channelsLoading: boolean;
  updatingSettings: boolean;
  updatingRule: boolean;
  onToggleSlack: (enabled: boolean) => void;
  onUpdateRule: (
    ruleId: string,
    data: Partial<
      Pick<
        CrmNotificationRule,
        "isEnabled" | "slackChannelId" | "slackChannelName"
      >
    >,
  ) => void;
};

/**
 * CRM通知設定ページのプレゼンテーションコンポーネント
 * @param {object} props コンポーネントProps
 * @param {CrmNotificationSettings | null} props.settings 通知設定
 * @param {boolean} props.settingsLoading 設定ロード中
 * @param {SlackIntegration | null} props.slackIntegration Slack連携情報
 * @param {boolean} props.slackLoading Slack情報ロード中
 * @param {SlackChannel[]} props.channels Slackチャンネル一覧
 * @param {boolean} props.channelsLoading チャンネルロード中
 * @param {boolean} props.updatingSettings マスター設定更新中
 * @param {boolean} props.updatingRule ルール更新中
 * @param {Function} props.onToggleSlack Slack通知トグルハンドラ
 * @param {Function} props.onUpdateRule ルール更新ハンドラ
 * @return {React.ReactElement} CRM通知設定ページ
 */
export const CrmNotificationSettingsPage: React.FC<
  CrmNotificationSettingsPageProps
> = ({
  settings,
  settingsLoading,
  slackIntegration,
  slackLoading,
  channels,
  channelsLoading,
  updatingSettings,
  updatingRule,
  onToggleSlack,
  onUpdateRule,
}) => {
  const isSlackConnected = slackIntegration?.isConnected ?? false;
  const isSlackEnabled = settings?.slackEnabled ?? false;
  const isDisabled = !isSlackConnected || !isSlackEnabled;

  if (settingsLoading || slackLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* ページタイトル */}
      <h1 className="text-2xl font-bold">CRM通知設定</h1>

      {/* Slack連携ステータスカード */}
      <div className="card bg-base-100 shadow-sm border border-base-200">
        <div className="card-body p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isSlackConnected ? (
                <>
                  <CheckCircle className="w-5 h-5 text-success" />
                  <div>
                    <StatusBadge tone="soft-success">Slack連携済み</StatusBadge>
                    {slackIntegration?.teamName && (
                      <span className="ml-2 text-sm text-base-content/70">
                        {slackIntegration.teamName}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  <div className="flex items-center gap-2">
                    <StatusBadge tone="soft-warning">Slack未連携</StatusBadge>
                    <span className="inline-flex items-center gap-1 text-sm">
                      <Link to="/settings/tenant">テナント設定で連携する</Link>
                      <ExternalLink className="w-3.5 h-3.5 text-violet-600" />
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* マスタートグルカード */}
      <div className="card bg-base-100 shadow-sm border border-base-200">
        <div className="card-body p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">Slack通知を有効にする</h2>
              <p className="text-sm text-base-content/60 mt-1">
                CRMイベントのSlack通知を一括で有効/無効にします
              </p>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={isSlackEnabled}
              disabled={!isSlackConnected || updatingSettings}
              onChange={e => onToggleSlack(e.target.checked)}
            />
          </div>
        </div>
      </div>

      {/* ルールテーブルカード */}
      <div className="card bg-base-100 shadow-sm border border-base-200">
        <div className="card-body p-5">
          <h2 className="text-base font-semibold mb-4">通知ルール</h2>
          {settings?.rules && settings.rules.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>イベント種別</th>
                    <th>送信先チャネル</th>
                    <th className="text-center">有効/無効</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.rules.map(rule => (
                    <RuleRow
                      key={rule.id}
                      rule={rule}
                      channels={channels}
                      channelsLoading={channelsLoading}
                      disabled={isDisabled}
                      updating={updatingRule}
                      onUpdateRule={onUpdateRule}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-base-content/60">
              通知ルールがありません
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ===============================
// ルール行コンポーネント
// ===============================

type RuleRowProps = {
  rule: CrmNotificationRule;
  channels: SlackChannel[];
  channelsLoading: boolean;
  disabled: boolean;
  updating: boolean;
  onUpdateRule: (
    ruleId: string,
    data: Partial<
      Pick<
        CrmNotificationRule,
        "isEnabled" | "slackChannelId" | "slackChannelName"
      >
    >,
  ) => void;
};

/**
 * 通知ルール行コンポーネント
 * @param {object} props コンポーネントProps
 * @param {CrmNotificationRule} props.rule ルール情報
 * @param {SlackChannel[]} props.channels Slackチャンネル一覧
 * @param {boolean} props.channelsLoading チャンネルロード中
 * @param {boolean} props.disabled 無効化フラグ
 * @param {boolean} props.updating 更新中フラグ
 * @param {Function} props.onUpdateRule ルール更新ハンドラ
 * @return {React.ReactElement} ルール行
 */
const RuleRow: React.FC<RuleRowProps> = ({
  rule,
  channels,
  channelsLoading,
  disabled,
  updating,
  onUpdateRule,
}) => {
  /**
   * チャンネル変更ハンドラ
   * @param {string} channelId 選択されたチャンネルID
   * @return {void}
   */
  const handleChannelChange = (channelId: string): void => {
    const channel = channels.find(c => c.id === channelId);
    onUpdateRule(rule.id, {
      slackChannelId: channelId || null,
      slackChannelName: channel?.name ?? null,
    });
  };

  /**
   * トグル変更ハンドラ
   * @param {boolean} isEnabled 有効/無効
   * @return {void}
   */
  const handleToggleChange = (isEnabled: boolean): void => {
    onUpdateRule(rule.id, { isEnabled });
  };

  return (
    <tr>
      <td className="font-medium">{rule.eventTypeLabel}</td>
      <td>
        <select
          className="select select-bordered select-sm w-full max-w-xs"
          value={rule.slackChannelId ?? ""}
          disabled={disabled || updating || channelsLoading}
          onChange={e => handleChannelChange(e.target.value)}
        >
          <option value="">チャンネルを選択</option>
          {channels.map(channel => (
            <option key={channel.id} value={channel.id}>
              #{channel.name}
            </option>
          ))}
        </select>
      </td>
      <td className="text-center">
        <input
          type="checkbox"
          className="toggle toggle-sm"
          checked={rule.isEnabled}
          disabled={disabled || updating}
          onChange={e => handleToggleChange(e.target.checked)}
        />
      </td>
    </tr>
  );
};
